import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { aiCreditsAPI, aiUsageAPI, creditTransactionsAPI } from '../services/api';
import { format } from 'date-fns';

const AICredits = () => {
  const [credits, setCredits] = useState([]);
  const [stats, setStats] = useState(null);
  const [usage, setUsage] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);
  const [activeTab, setActiveTab] = useState('credits');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [addCreditsForm, setAddCreditsForm] = useState({ credits: 100, reason: '' });
  // NR-357: monthly free quota state
  const [globalQuota, setGlobalQuota] = useState(null);
  const [globalQuotaInput, setGlobalQuotaInput] = useState('');
  const [savingGlobalQuota, setSavingGlobalQuota] = useState(false);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [quotaInput, setQuotaInput] = useState('');
  const [savingQuota, setSavingQuota] = useState(false);

  // Credit packages with pricing ($0.01 per credit)
  const CREDIT_PACKAGES = [
    { credits: 100, price: 1, label: '100 Credits' },
    { credits: 500, price: 5, label: '500 Credits' },
    { credits: 1000, price: 10, label: '1,000 Credits' },
    { credits: 5000, price: 45, label: '5,000 Credits', discount: '10% off' },
    { credits: 10000, price: 80, label: '10,000 Credits', discount: '20% off' },
  ];
  const [filters, setFilters] = useState({
    license_type: '',
    low_credits: false
  });

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.license_type) params.license_type = filters.license_type;
      if (filters.low_credits) params.low_credits = 'true';

      const [creditsRes, statsRes, usageRes, transactionsRes] = await Promise.all([
        aiCreditsAPI.list(params),
        aiCreditsAPI.stats(),
        aiUsageAPI.list({ limit: 50 }),
        creditTransactionsAPI.list({ limit: 50 })
      ]);

      setCredits(creditsRes.data.results || creditsRes.data || []);
      setStats(statsRes.data);
      setUsage(usageRes.data.results || usageRes.data || []);
      setTransactions(transactionsRes.data.results || transactionsRes.data || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  // NR-357: load the global monthly quota once.
  useEffect(() => {
    aiCreditsAPI.getGlobalQuota()
      .then((res) => {
        setGlobalQuota(res.data.monthly_quota);
        setGlobalQuotaInput(String(res.data.monthly_quota));
      })
      .catch((err) => console.error('Failed to load global quota:', err));
  }, []);

  const handleSaveGlobalQuota = async () => {
    const val = parseInt(globalQuotaInput, 10);
    if (isNaN(val) || val < 0) { alert('Enter a valid non-negative number.'); return; }
    setSavingGlobalQuota(true);
    try {
      const res = await aiCreditsAPI.setGlobalQuota(val);
      setGlobalQuota(res.data.monthly_quota);
      fetchData();  // refresh effective quotas that fall back to global
    } catch (err) {
      alert('Failed to save global quota: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingGlobalQuota(false);
    }
  };

  const openQuotaModal = (credit) => {
    setSelectedLicense(credit);
    setQuotaInput(credit.monthly_quota_override != null ? String(credit.monthly_quota_override) : '');
    setShowQuotaModal(true);
  };

  const handleSaveQuota = async () => {
    // Empty input => clear override (null) => fall back to global default.
    const override = quotaInput.trim() === '' ? null : parseInt(quotaInput, 10);
    if (override !== null && (isNaN(override) || override < 0)) {
      alert('Enter a non-negative number, or leave blank to use the global default.');
      return;
    }
    setSavingQuota(true);
    try {
      await aiCreditsAPI.setQuota(selectedLicense.license_id, override);
      setShowQuotaModal(false);
      fetchData();
    } catch (err) {
      alert('Failed to save quota: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingQuota(false);
    }
  };

  const handleAddCredits = async () => {
    if (!selectedLicense) return;
    console.log('[AddCredits] Sending:', { license: selectedLicense.license, form: addCreditsForm });
    try {
      await aiCreditsAPI.addCredits(selectedLicense.license, addCreditsForm);
      setShowAddModal(false);
      setAddCreditsForm({ credits: 100, reason: '' });
      setSelectedLicense(null);
      fetchData();
    } catch (err) {
      console.error('Failed to add credits:', err);
      const errorData = err.response?.data;
      const errorMsg = errorData?.error || errorData?.credits?.[0] || JSON.stringify(errorData) || 'Failed to add credits';
      alert(errorMsg);
    }
  };

  const openAddModal = (credit) => {
    setSelectedLicense(credit);
    setShowAddModal(true);
  };

  const handleInitializeAll = async () => {
    if (!window.confirm('This will create credit records for all active licenses that don\'t have one. Continue?')) {
      return;
    }
    setInitializing(true);
    try {
      const res = await aiCreditsAPI.initializeAll();
      alert(`Success! Created credit records for ${res.data.created_count} licenses.`);
      fetchData();
    } catch (err) {
      console.error('Failed to initialize credits:', err);
      alert(err.response?.data?.error || 'Failed to initialize credits');
    } finally {
      setInitializing(false);
    }
  };

  const getCreditStatusBadge = (creditInfo) => {
    if (!creditInfo) return null;
    const { credits_available, total_credits, credits_used } = creditInfo;
    const percentUsed = total_credits > 0 ? (credits_used / total_credits) * 100 : 0;

    if (credits_available <= 0) {
      return <span className="badge badge-danger">Exhausted</span>;
    }
    if (credits_available <= 10) {
      return <span className="badge badge-warning">Low</span>;
    }
    if (percentUsed >= 80) {
      return <span className="badge badge-warning">High Usage</span>;
    }
    return <span className="badge badge-success">Good</span>;
  };

  const getLicenseTypeBadge = (type) => {
    const colors = {
      trial: 'badge-gray',
      single: 'badge-info',
      team: 'badge-purple',
      enterprise: 'badge-success'
    };
    return <span className={`badge ${colors[type] || 'badge-gray'}`}>{type}</span>;
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">AI Credits</h1>
          <p className="page-description">Manage AI credit allocations and monitor usage</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={handleInitializeAll}
          disabled={initializing}
        >
          {initializing ? 'Initializing...' : 'Initialize Credits for All Licenses'}
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <div className="card" style={{ padding: '1.25rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary)' }}>{stats.total_licenses}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Total Licenses</div>
          </div>
          <div className="card" style={{ padding: '1.25rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--success)' }}>{stats.total_credits_issued?.toLocaleString()}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Total Credits Issued</div>
          </div>
          <div className="card" style={{ padding: '1.25rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--info)' }}>{stats.total_credits_used?.toLocaleString()}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Total Credits Used</div>
          </div>
          <div className="card" style={{ padding: '1.25rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: stats.low_credit_licenses > 0 ? 'var(--warning)' : 'var(--success)' }}>
              {stats.low_credit_licenses}
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Low Credit Licenses</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="card">
        <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: '1rem' }}>
          {['credits', 'usage', 'transactions'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                borderBottom: activeTab === tab ? '2px solid var(--primary)' : '2px solid transparent',
                color: activeTab === tab ? 'var(--primary)' : 'var(--text-secondary)',
                fontWeight: activeTab === tab ? '600' : '400',
                textTransform: 'capitalize'
              }}
            >
              {tab === 'credits' ? 'Credit Balances' : tab === 'usage' ? 'Usage History' : 'Transactions'}
            </button>
          ))}
        </div>

        {/* Credits Tab */}
        {activeTab === 'credits' && (
          <>
            {/* NR-357: Global monthly free quota (applies to every customer without
                a per-license override). */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
              marginBottom: '1.25rem', padding: '0.9rem 1.1rem',
              background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.75rem'
            }}>
              <div style={{ fontWeight: 600, color: '#334155' }}>Global monthly free quota</div>
              <input
                type="number" min="0"
                className="form-input"
                style={{ width: '140px' }}
                value={globalQuotaInput}
                onChange={(e) => setGlobalQuotaInput(e.target.value)}
                placeholder="e.g. 10000"
              />
              <span style={{ fontSize: '0.8rem', color: '#64748b' }}>credits / month · 1 credit = 500 tokens</span>
              <button
                className="btn btn-sm btn-primary"
                onClick={handleSaveGlobalQuota}
                disabled={savingGlobalQuota || String(globalQuota) === globalQuotaInput}
              >
                {savingGlobalQuota ? 'Saving…' : 'Save global quota'}
              </button>
              <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: '#94a3b8' }}>
                Per-customer overrides are set with “Set Quota” in the table below.
              </span>
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              <select
                className="form-select"
                style={{ width: 'auto' }}
                value={filters.license_type}
                onChange={(e) => setFilters({ ...filters, license_type: e.target.value })}
              >
                <option value="">All License Types</option>
                <option value="trial">Trial</option>
                <option value="single">Single</option>
                <option value="team">Team</option>
                <option value="enterprise">Enterprise</option>
              </select>

              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={filters.low_credits}
                  onChange={(e) => setFilters({ ...filters, low_credits: e.target.checked })}
                />
                Show Low Credits Only
              </label>
            </div>

            {loading ? (
              <div className="loading-container"><div className="spinner"></div></div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>License Type</th>
                      <th>Total Credits</th>
                      <th>Used</th>
                      <th>Available</th>
                      <th>Monthly Quota</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {credits.length === 0 ? (
                      <tr>
                        <td colSpan="8" style={{ textAlign: 'center', padding: '2rem' }}>
                          No credit records found.
                        </td>
                      </tr>
                    ) : (
                      credits.map((credit) => (
                        <tr key={credit.license}>
                          <td>
                            <Link to={`/dashboard/licenses/${credit.license_id}`} className="font-medium" style={{ color: 'var(--primary)' }}>
                              {credit.customer_name}
                            </Link>
                          </td>
                          <td>{getLicenseTypeBadge(credit.license_type)}</td>
                          <td>{credit.credits?.toLocaleString()}</td>
                          <td>{credit.credit_info?.credits_used?.toLocaleString()}</td>
                          <td style={{ fontWeight: '600' }}>{credit.credit_info?.credits_available?.toLocaleString()}</td>
                          {/* NR-357: monthly free quota — shows effective value; "custom"
                              badge when a per-license override is set, else "default". */}
                          <td>
                            <span style={{ fontWeight: '600' }}>
                              {(credit.effective_monthly_quota ?? 0).toLocaleString()}
                            </span>
                            <span style={{
                              marginLeft: '0.4rem', fontSize: '0.7rem', padding: '1px 6px',
                              borderRadius: '999px',
                              background: credit.monthly_quota_override != null ? '#eef2ff' : '#f1f5f9',
                              color: credit.monthly_quota_override != null ? '#4338ca' : '#64748b',
                            }}>
                              {credit.monthly_quota_override != null ? 'custom' : 'default'}
                            </span>
                          </td>
                          <td>{getCreditStatusBadge(credit.credit_info)}</td>
                          <td style={{ display: 'flex', gap: '0.4rem' }}>
                            <button className="btn btn-sm btn-primary" onClick={() => openAddModal(credit)}>
                              Add Credits
                            </button>
                            <button className="btn btn-sm btn-secondary" onClick={() => openQuotaModal(credit)}>
                              Set Quota
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Usage Tab */}
        {activeTab === 'usage' && (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>License Type</th>
                  <th>Requests</th>
                  <th>Input Tokens</th>
                  <th>Output Tokens</th>
                  <th>Total Tokens</th>
                  <th>Credits Used</th>
                </tr>
              </thead>
              <tbody>
                {usage.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '2rem' }}>
                      No usage records found.
                    </td>
                  </tr>
                ) : (
                  usage.map((record) => (
                    <tr key={record.id}>
                      <td>{format(new Date(record.date), 'MMM d, yyyy')}</td>
                      <td>{record.customer_name}</td>
                      <td>{getLicenseTypeBadge(record.license_type)}</td>
                      <td>{record.request_count}</td>
                      <td>{record.input_tokens?.toLocaleString()}</td>
                      <td>{record.output_tokens?.toLocaleString()}</td>
                      <td>{record.total_tokens?.toLocaleString()}</td>
                      <td style={{ fontWeight: '600' }}>{record.credits_used}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Type</th>
                  <th>Credits</th>
                  <th>Reason</th>
                  <th>Performed By</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>
                      No transactions found.
                    </td>
                  </tr>
                ) : (
                  transactions.map((tx) => (
                    <tr key={tx.id}>
                      <td>{format(new Date(tx.created_at), 'MMM d, yyyy HH:mm')}</td>
                      <td>{tx.customer_name}</td>
                      <td>
                        <span className={`badge ${tx.transaction_type === 'add' ? 'badge-success' : tx.transaction_type === 'purchase' ? 'badge-info' : 'badge-gray'}`}>
                          {tx.transaction_type}
                        </span>
                      </td>
                      <td style={{ color: tx.credits > 0 ? 'var(--success)' : 'var(--danger)', fontWeight: '600' }}>
                        {tx.credits > 0 ? `+${tx.credits}` : tx.credits}
                      </td>
                      <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {tx.reason || '-'}
                      </td>
                      <td>{tx.performed_by_name || 'System'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Credits Modal */}
      {showAddModal && selectedLicense && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Credits</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                Adding credits to: <strong>{selectedLicense.customer_name}</strong>
              </p>

              <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.9rem' }}>
                  <div>Total Credits: <strong>{selectedLicense.credits}</strong></div>
                  <div>Used: <strong>{selectedLicense.credit_info?.credits_used}</strong></div>
                  <div>Available: <strong>{selectedLicense.credit_info?.credits_available}</strong></div>
                  <div>License Type: <strong>{selectedLicense.license_type}</strong></div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Select Package</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.5rem', marginBottom: '1rem' }}>
                  {CREDIT_PACKAGES.map((pkg) => (
                    <button
                      key={pkg.credits}
                      type="button"
                      onClick={() => setAddCreditsForm({ ...addCreditsForm, credits: pkg.credits })}
                      style={{
                        padding: '0.75rem',
                        border: addCreditsForm.credits === pkg.credits ? '2px solid var(--primary)' : '1px solid #d1d5db',
                        borderRadius: '8px',
                        background: addCreditsForm.credits === pkg.credits ? 'var(--primary-light, #e0e7ff)' : 'white',
                        cursor: 'pointer',
                        textAlign: 'center',
                        position: 'relative'
                      }}
                    >
                      <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>{pkg.label}</div>
                      <div style={{ color: 'var(--success)', fontWeight: '600' }}>${pkg.price}</div>
                      {pkg.discount && (
                        <span style={{
                          position: 'absolute',
                          top: '-8px',
                          right: '-8px',
                          background: 'var(--warning)',
                          color: 'white',
                          fontSize: '0.65rem',
                          padding: '2px 6px',
                          borderRadius: '10px',
                          fontWeight: '600'
                        }}>
                          {pkg.discount}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Or enter custom amount</label>
                <input
                  type="number"
                  className="form-input"
                  value={addCreditsForm.credits}
                  onChange={(e) => setAddCreditsForm({ ...addCreditsForm, credits: parseInt(e.target.value) || 0 })}
                  min="1"
                  max="100000"
                />
                <small style={{ color: 'var(--text-secondary)' }}>
                  1 credit = 1,000 tokens | Price: ${(addCreditsForm.credits * 0.01).toFixed(2)}
                </small>
              </div>

              <div className="form-group">
                <label className="form-label">Reason (optional)</label>
                <textarea
                  className="form-input"
                  value={addCreditsForm.reason}
                  onChange={(e) => setAddCreditsForm({ ...addCreditsForm, reason: e.target.value })}
                  rows={2}
                  placeholder="e.g., Promotional credits, compensation, etc."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleAddCredits} disabled={addCreditsForm.credits <= 0}>
                Add {addCreditsForm.credits} Credits
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NR-357: Per-license Monthly Quota Modal */}
      {showQuotaModal && selectedLicense && (
        <div className="modal-overlay" onClick={() => setShowQuotaModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Set Monthly Quota</h2>
              <button className="modal-close" onClick={() => setShowQuotaModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                Monthly free credits for: <strong>{selectedLicense.customer_name}</strong>
              </p>
              <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.9rem' }}>
                <div>Global default: <strong>{globalQuota != null ? globalQuota.toLocaleString() : '—'}</strong> credits/month</div>
                <div style={{ marginTop: '0.25rem' }}>
                  Current effective: <strong>{(selectedLicense.effective_monthly_quota ?? 0).toLocaleString()}</strong>
                  {' '}({selectedLicense.monthly_quota_override != null ? 'custom override' : 'using global default'})
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Monthly quota override</label>
                <input
                  type="number" min="0"
                  className="form-input"
                  value={quotaInput}
                  onChange={(e) => setQuotaInput(e.target.value)}
                  placeholder={`Leave blank to use global default (${globalQuota != null ? globalQuota.toLocaleString() : '10000'})`}
                  autoFocus
                />
                <small style={{ color: 'var(--text-secondary)' }}>
                  Blank = use the global default. 1 credit = 500 tokens. Users needing more can purchase credits.
                </small>
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '1rem' }}>
              <button className="btn btn-secondary" onClick={() => setShowQuotaModal(false)} disabled={savingQuota}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveQuota} disabled={savingQuota}>
                {savingQuota ? 'Saving…' : 'Save quota'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          backdrop-filter: blur(4px);
        }
        .modal {
          background: #ffffff;
          border-radius: 12px;
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid #e5e7eb;
        }
        .modal-header h2 {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
        }
        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #6b7280;
        }
        .modal-body {
          padding: 1.5rem;
        }
        .modal-body .form-group {
          margin-bottom: 1.25rem;
        }
        .modal-body .form-label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 500;
        }
        .modal-body .form-input {
          width: 100%;
          padding: 0.625rem 0.875rem;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.95rem;
        }
        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1rem 1.5rem;
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
        }
        .badge-purple {
          background: #8b5cf6;
          color: white;
        }
        .badge-info {
          background: #0ea5e9;
          color: white;
        }
      `}</style>
    </div>
  );
};

export default AICredits;
